import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, of } from "rxjs";
import { first, map, shareReplay, tap } from "rxjs/operators";
import { CookieService } from "ngx-cookie-service";
import { environment } from "src/environments/environment";

/** Tipo de documento definido pela entidade (Caderneta Predial, Certidão…). */
export interface PropertyDocumentType {
  id: number;
  name: string;
  defaultVisibility: "COMMERCIAL" | "INTERNAL" | "PRIVATE";
  order: number | null;
  active: boolean;
  /** Ligação automática a um documento do sistema (ex.: CMI_SIGNED). */
  systemKey?: PropertyDocumentSystemKey | null;
  filesCount?: number;
}

/** Documentos que a plataforma produz e pode arrumar sozinha no imóvel. */
export type PropertyDocumentSystemKey = "CMI_SIGNED";
export const PROPERTY_DOCUMENT_SYSTEM_KEYS: PropertyDocumentSystemKey[] = [
  "CMI_SIGNED",
];

/** Regra: tipo de documento × grupo (null = todos) × estados × negócios. */
export interface PropertyDocumentRule {
  id?: number;
  documentTypeId: number;
  propertyTypeGroupId: number | null;
  propertyConditionIds: number[];
  propertyBusinessTypeIds: number[];
  isRequired: boolean;
}

export interface PropertyDocumentsConfig {
  enabled: boolean;
  since: string | null;
  documentTypes: PropertyDocumentType[];
  rules: PropertyDocumentRule[];
  lookups: {
    groups: Array<{ id: number; description: string; key?: string | null }>;
    conditions: Array<{ id: number; description: string }>;
    businessTypes: Array<{ id: number; description: string }>;
  };
}

/** Uma linha da checklist de um imóvel. */
export interface PropertyDocumentChecklistItem {
  documentTypeId: number;
  name: string;
  defaultVisibility: "COMMERCIAL" | "INTERNAL" | "PRIVATE";
  isRequired: boolean;
  order: number;
  done: boolean;
  files: Array<{
    id?: number;
    originalName?: string | null;
    fileName?: string | null;
    propertyDocumentTypeId?: number | null;
  }>;
}

export interface PropertyDocumentChecklist {
  /** Entidade com a regra ligada e imóvel criado depois de a ligar. */
  typedMode: boolean;
  items: PropertyDocumentChecklistItem[];
  requiredMissing: number;
  requiredTotal: number;
}

export interface ApplicableDocumentsQuery {
  propertyId?: string | null;
  propertyTypeId?: number | string | null;
  propertyConditionId?: number | string | null;
  propertyBusinessTypeId?: number | string | null;
}

/**
 * Documentos obrigatórios por tipo de imóvel: configuração (MASTER), tipos
 * ativos para o `ng-select` do separador de documentos e checklist de um
 * imóvel. Os tipos ativos ficam em cache — são poucos e mudam raramente.
 */
@Injectable({ providedIn: "root" })
export class PropertyDocumentsService {
  private readonly base = `${environment.apiUrl}/property-documents`;

  private activeTypes: PropertyDocumentType[] | null = null;
  private activeTypes$: Observable<PropertyDocumentType[]> | null = null;

  constructor(
    private http: HttpClient,
    private cookies: CookieService,
  ) {}

  // ------------------------------------------------------------ configuração

  getConfig(): Observable<PropertyDocumentsConfig> {
    const lang = this.cookies.get("lang") || "pt";
    return this.http
      .get<PropertyDocumentsConfig>(`${this.base}/config/${lang}`, {
        withCredentials: true,
      })
      .pipe(first());
  }

  updateSettings(enabled: boolean) {
    return this.http
      .patch<{ enabled: boolean; since: string | null }>(
        `${this.base}/settings`,
        { enabled },
        { withCredentials: true },
      )
      .pipe(first(), tap(() => this.invalidateTypes()));
  }

  createType(data: {
    name: string;
    defaultVisibility?: string;
    order?: number;
    systemKey?: PropertyDocumentSystemKey | null;
  }) {
    return this.http
      .post<PropertyDocumentType>(`${this.base}/types`, data, {
        withCredentials: true,
      })
      .pipe(first(), tap(() => this.invalidateTypes()));
  }

  updateType(
    id: number,
    data: {
      name?: string;
      defaultVisibility?: string;
      order?: number;
      active?: boolean;
      systemKey?: PropertyDocumentSystemKey | null;
    },
  ) {
    return this.http
      .patch<PropertyDocumentType>(`${this.base}/types/${id}`, data, {
        withCredentials: true,
      })
      .pipe(first(), tap(() => this.invalidateTypes()));
  }

  deactivateType(id: number) {
    return this.http
      .delete<PropertyDocumentType>(`${this.base}/types/${id}`, {
        withCredentials: true,
      })
      .pipe(first(), tap(() => this.invalidateTypes()));
  }

  saveRules(rules: PropertyDocumentRule[]) {
    return this.http
      .put<PropertyDocumentRule[]>(
        `${this.base}/rules`,
        {
          rules: rules.map((r) => ({
            documentTypeId: r.documentTypeId,
            propertyTypeGroupId: r.propertyTypeGroupId ?? null,
            propertyConditionIds: r.propertyConditionIds ?? [],
            propertyBusinessTypeIds: r.propertyBusinessTypeIds ?? [],
            isRequired: !!r.isRequired,
          })),
        },
        { withCredentials: true },
      )
      .pipe(first());
  }

  // ---------------------------------------------------------------- consulta

  /** Tipos ativos da entidade, com cache. */
  getActiveTypes(force = false): Observable<PropertyDocumentType[]> {
    if (!force && this.activeTypes) return of(this.activeTypes);
    if (!force && this.activeTypes$) return this.activeTypes$;
    this.activeTypes$ = this.http
      .get<PropertyDocumentType[]>(`${this.base}/types`, {
        withCredentials: true,
      })
      .pipe(
        map((rows) => rows ?? []),
        tap((rows) => {
          this.activeTypes = rows;
          this.activeTypes$ = null;
        }),
        shareReplay(1),
      );
    return this.activeTypes$;
  }

  invalidateTypes() {
    this.activeTypes = null;
    this.activeTypes$ = null;
  }

  /**
   * Reclassificar um documento do imóvel já carregado (tipo e/ou
   * visibilidade) sem re-gravar o formulário do imóvel.
   */
  updateFile(
    fileId: number,
    data: {
      propertyDocumentTypeId?: number | null;
      documentVisibility?: string | null;
    },
  ) {
    const body: any = {};
    if (data.propertyDocumentTypeId !== undefined) {
      body.propertyDocumentTypeId = data.propertyDocumentTypeId;
    }
    if (data.documentVisibility) {
      body.documentVisibility = data.documentVisibility;
    }
    return this.http
      .patch<any>(`${this.base}/files/${fileId}`, body, {
        withCredentials: true,
      })
      .pipe(first());
  }

  /** Checklist para um imóvel gravado ou ainda no formulário. */
  getApplicable(
    query: ApplicableDocumentsQuery,
  ): Observable<PropertyDocumentChecklist> {
    let params = new HttpParams();
    const set = (k: string, v: any) => {
      if (v === null || v === undefined || v === "" || v === 0) return;
      params = params.set(k, `${v}`);
    };
    set("propertyId", query.propertyId);
    set("propertyTypeId", query.propertyTypeId);
    set("propertyConditionId", query.propertyConditionId);
    set("propertyBusinessTypeId", query.propertyBusinessTypeId);
    return this.http
      .get<PropertyDocumentChecklist>(`${this.base}/applicable`, {
        params,
        withCredentials: true,
      })
      .pipe(first());
  }
}
